import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useUpdateassetReqMutation, useUpdateStorePartialAssetMutation } from '../../../../features/assetrequest/assetReqApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import { useGetAssetsFinalQuery, useLazyGetAssetsFinalFilteredQuery, useUpdateMultipleDocsMutation } from '../../../../features/addassets/addassetfinalApiSlice';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import UnivMyAssetComp from '../../Myasset/UnivMyAssetComp';

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ActionButton = styled(Button)`
  background-color: #1976d2;
`;

const CreateIssueButton = styled(Button)`
  background-color: #4caf50;
`;

const ViewAssetsButton = styled(Button)`
  background-color: #ff9800;
`;

const ForwardButton = styled(Button)`
  background-color: #4caf50;
`;

const RejectButton = styled(Button)`
  background-color: #f44336;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

const ModalTitle = styled.h2`
  margin-bottom: 20px;
  color: #1976d2;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 10px;
`;

const SelectedAssetsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  max-width: 95%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px;
  background-color: #f9f9f9;
`;

const SelectedAssetsTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 16px;
  color: #1976d2;
  font-size: 16px;
  width: 100%;
`;

const InfoMessage = styled.div`
  background-color: #e3f2fd;
  padding: 10px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  color: #1976d2;
  font-size: 14px;
`;

const StoreL1InboxForm = ({ row, handleBack }) => {
  const [remark, setRemark] = useState('');
  const [openCreateIssueModal, setOpenCreateIssueModal] = useState(false);
  const [openViewAssetsModal, setOpenViewAssetsModal] = useState(false);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [requestedOfficeAssets, setRequestedOfficeAssets] = useState([]);
  const [assetDetails, setAssetDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [assetsConfirmed, setAssetsConfirmed] = useState(false);
  const [modalInstanceId, setModalInstanceId] = useState(1);

  const auth = useAuth();
  const user_current_office = useSelector((state) => state.office.off);

  // Memoize the filter props to prevent recreating objects on every render
  const storeFilterProps = useMemo(() => ({
    cur_loc: ['store'],
    in_transit:"false",
    cur_store: [user_current_office],
    perm_name: row.assets_requested[row.assets_requested.length - 1]?.data?.map( x => x.assetName)
  }), [user_current_office]);

  const userOfficeFilterProps = useMemo(() => ({
    alloc_ofc: [row?.req_data?.[0]?.sent_by?.office]
  }), [row?.req_data]);

  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { storeoffices } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });

  const [updateReq] = useUpdateassetReqMutation();
  const [updateStorePartialAsset] = useUpdateStorePartialAssetMutation();
  const [fetchFilteredAssets] = useLazyGetAssetsFinalFilteredQuery();
  const [updateMultipleAssetDocs] = useUpdateMultipleDocsMutation();
  

  // Check if request was forwarded by user after accepting partial assets
  const isPartialAcceptedForwardStore = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'ureq_paraccept_fwdstore';
  
  // Get the previously partially issued assets
  const partialIssuedAssetIds = row?.bo_assets_issue?.length > 0 
    ? (row.bo_assets_issue[row.bo_assets_issue.length - 1]?.assets || []) 
    : [];

  // Get the originally requested assets
  const originalRequestedAssets = row?.assets_requested?.length > 0 
    ? row.assets_requested[row.assets_requested.length - 1].data 
    : [];

  // Add function to get a summary of asset counts
  const getAssetSummary = useMemo(() => {
    const summary = {};
    
    // Get requested quantities
    if (originalRequestedAssets && originalRequestedAssets.length > 0) {
      originalRequestedAssets.forEach(asset => {
        const key = `${asset.catName}-${asset.assetName}`;
        if (!summary[key]) {
          summary[key] = {
            category: asset.catName,
            name: asset.assetName,
            requested: 0,
            issued: 0,
            remaining: 0,
            selected: 0
          };
        }
        summary[key].requested += parseInt(asset.quantity, 10);
      });
    }
    
    // Count already issued assets - only from the most recent BO assets issue
    if (partialIssuedAssetIds && partialIssuedAssetIds.length > 0 && Object.keys(assetDetails).length > 0) {
      partialIssuedAssetIds.forEach(assetId => {
        const asset = assetDetails[assetId];
        if (asset) {
          const key = `${asset.category}-${asset.name}`;
          if (summary[key]) {
            summary[key].issued += 1;
          }
        }
      });
    }
    
    // Count selected assets
    selectedAssets.forEach(asset => {
      const key = `${asset.category}-${asset.name}`;
      if (summary[key]) {
        summary[key].selected += 1;
      }
    });
    
    // Calculate remaining
    Object.values(summary).forEach(item => {
      item.remaining = item.requested - item.issued;
    });
    
    return Object.values(summary);
  }, [originalRequestedAssets, partialIssuedAssetIds, assetDetails, selectedAssets]);

  // Fetch asset details when partial assets are available
  useEffect(() => {
    if (partialIssuedAssetIds.length > 0 && Object.keys(assetDetails).length === 0) {
      fetchFilteredAssets({
        page: 1,
        limit: 999,
        filters: {
          ids: partialIssuedAssetIds
        }
      }).then(response => {
        if (response?.data?.assets) {
          const detailsMap = {};
          response.data.assets.forEach(asset => {
            detailsMap[asset._id] = asset;
          });
          setAssetDetails(detailsMap);
        }
      });
    }
  }, [partialIssuedAssetIds, fetchFilteredAssets, assetDetails]);

  // Fetch assets from the requesting office only once when component mounts
  useEffect(() => {
    if (row?.req_creator_id && requestedOfficeAssets.length === 0) {
      fetchFilteredAssets({
        page: 1,
        limit: 999,
        filters: {
          alloc_ofc: row.req_data?.[0]?.sent_by?.office
        }
      }).then(response => {
        if (response?.data?.assets) {
          setRequestedOfficeAssets(response.data.assets);
        }
      });
    }
  }, [row?.req_creator_id, row?.req_data, fetchFilteredAssets, requestedOfficeAssets.length]);

  // Get L2ReqApprover of the current store
  const getStoreLevel2Approver = () => {
    if (!row?.req_store || !storeoffices) return null;
    
    const storeOffice = Object.values(storeoffices).find(store => 
      store.storeoffice === row.req_store
    );
    
    return storeOffice?.roleUsers?.L2ReqApprover_A || null;
  };

  // Handler for opening the create issue modal
  const handleOpenCreateIssueModal = () => {
    // Start with no assets selected and reset confirmation
    setSelectedAssets([]);
    setAssetsConfirmed(false);
    // Increment instance ID to force component refresh
    setModalInstanceId(prevId => prevId + 1);
    setOpenCreateIssueModal(true);
  };

  // Handler for closing the create issue modal
  const handleCloseCreateIssueModal = () => {
    // Always reset selected assets when canceling
    setSelectedAssets([]);
    // Only reset assetsConfirmed if we haven't finalized the selection
    if (!assetsConfirmed) {
      setAssetsConfirmed(false);
    }
    setOpenCreateIssueModal(false);
  };

  // Handler for canceling the entire selection process
  const handleCancelSelection = () => {
    // Reset all selection-related state
    setSelectedAssets([]);
    setAssetsConfirmed(false);
    setOpenCreateIssueModal(false);
  };

  // Handler for confirming selected assets
  const handleConfirmSelectedAssets = () => {
    if (selectedAssets.length === 0) {
      alert("Please select at least one asset before confirming.");
      return;
    }
    
    // Check if any asset type exceeds remaining quantity
    const overAllocated = [];
    getAssetSummary.forEach(summary => {
      if (summary.selected > summary.remaining) {
        overAllocated.push({
          name: summary.name,
          selected: summary.selected,
          remaining: summary.remaining
        });
      }
    });
    
    if (overAllocated.length > 0) {
      const message = overAllocated.map(item => 
        `${item.name}: Selected ${item.selected}, Remaining: ${item.remaining}`
      ).join('\n');
      
      alert(`You have selected more assets than requested for the following:\n\n${message}\n\nPlease adjust your selection.`);
      return;
    }
    
    setAssetsConfirmed(true);
    setOpenCreateIssueModal(false);
  };

  // Handler for editing selected assets
  const handleEditSelectedAssets = () => {
    setOpenCreateIssueModal(true);
  };

  // Handler for opening the view assets modal
  const handleOpenViewAssetsModal = () => {
    setIsLoading(true);
    // Use a timeout to allow UI to update before heavy component loads
    setTimeout(() => {
      setOpenViewAssetsModal(true);
      setIsLoading(false);
    }, 100);
  };

  // Handler for closing the view assets modal
  const handleCloseViewAssetsModal = () => {
    setOpenViewAssetsModal(false);
  };

  // Handler for rejecting request
  const handleRejectRequest = async () => {
    if (!remark.trim()) {
      alert("Please provide a reason for rejecting the request.");
      return;
    }

    const level2Id = getStoreLevel2Approver();
    
    if (!level2Id) {
      alert("Store level 2 approver not found. Please ensure the store is properly configured.");
      return;
    }

    let senttoData = {
      sent_to_tag: "lvl1req_rejectn_forappvl",
      Text: remark,
      action: "Request Rejected by Store Level 1 Manager",
      sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: row?.req_store, designation: "Level1Manager" },
      sent_to: { id: level2Id, name: getSingleuser?.[level2Id]?.name, office: row?.req_store, designation: "Level2Manager" },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[level2Id])}-${row?.req_store}-Level2Manager`,
      time_stamp: new Date(),
      opened: false
    };
    
    const body = { id: row?._id, senttoData: senttoData };

    try {
      const res = await updateReq(body);
      alert("Request rejection sent to Level 2 Manager for approval");
      handleBack();
    } catch (error) {
      console.error("Error rejecting request:", error);
      alert("There was an error with the request. Please try again.");
    }
  };

  // Handler for forwarding request to Level 2 Manager with selected assets
  const handleForwardWithSelectedAssets = async () => {
    // Keep default remark logic but remove mandatory validation
    if (!remark && selectedAssets.length > 0) {
      setRemark("Assets selected for issue");
    }

    const level2Id = getStoreLevel2Approver();
    
    if (!level2Id) {
      alert("Store level 2 approver not found. Please ensure the store is properly configured.");
      return;
    }

    // Create asset IDs array from selected assets
    const selectedAssetIds = selectedAssets.map(asset => asset._id);
    
    console.log("Selected asset IDs:", selectedAssetIds);
    console.log("Request ID:", row?._id);

    let senttoData = {
      sent_to_tag: "lvl1req_fwd_selected_assets",
      Text: remark || "", // Use empty string if remark is not provided
      action: "Assets Selected for Issue by Store Level 1 Manager",
      sent_by: { id: auth?.id, name: getSingleuser?.[auth?.id]?.name, office: user_current_office, designation: getSingleuser?.[auth?.id]?.designation[user_current_office] },
      sent_to: { id: level2Id, name: getSingleuser?.[level2Id]?.name, office: row?.req_store, designation: getSingleuser?.[level2Id]?.designation[row?.req_store] },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[level2Id])}-${row?.req_store}-${getSingleuser?.[level2Id]?.designation[row?.req_store]}`,
      time_stamp: new Date().toISOString(),
    };

    const assetBody = {
      "assetIdArray": selectedAssetIds,
      "updates": {
        "in_transit": true,
      }
    }
    
    const body = { id: row?._id, senttoData: senttoData };
    const storePartialAssetBody = { id: row?._id, assets: selectedAssetIds, status: "draft" };
    
    console.log("Store partial asset body:", storePartialAssetBody);
    try {
      // First update the request flow
      const res = await updateReq(body);
      console.log("Request flow updated:", res);
      
      // Then create or update store partial assets
      const res2 = await updateStorePartialAsset(storePartialAssetBody);
      console.log("Store partial assets updated:", res2);

      const res3 = await updateMultipleAssetDocs(assetBody);
      
      alert("Request forwarded to Level 2 Manager with selected assets");
      handleCloseCreateIssueModal();
      handleBack();
    } catch (error) {
      console.error("Error forwarding request:", error);
      alert("There was an error with the request. Please try again.");
    }
  };

  // Handler for asset selection change from UnivMyAssetComp
  const handleSelectedAssetsChange = useCallback((newSelectedAssets) => {
    // Use a new array instance to ensure state is updated
    setSelectedAssets([...newSelectedAssets]);
  }, []);

  return (
    <>
      <RemarkContainer>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Add Remark"
          variant="outlined"
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
        />
      </RemarkContainer>

      {isPartialAcceptedForwardStore && (
        <InfoMessage>
          <strong>Note:</strong> This request has been forwarded by the user after accepting partial assets. Please create an issue with assets from your store to fulfill remaining requirements.
        </InfoMessage>
      )}

      {/* Asset Summary Table - Always show this */}
      <SelectedAssetsContainer style={{ marginBottom: '20px' }}>
        <SelectedAssetsTitle>Asset Request Summary</SelectedAssetsTitle>
        
        <TableContainer component={Paper} style={{ width: '100%' }}>
          <Table size="small" style={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell width="5%">Sl.No.</TableCell>
                <TableCell width="20%">Category</TableCell>
                <TableCell width="20%">Asset Name</TableCell>
                <TableCell align="center" width="15%">Requested</TableCell>
                <TableCell align="center" width="15%">Already Issued</TableCell>
                <TableCell align="center" width="15%">Remaining</TableCell>
                {assetsConfirmed && <TableCell align="center" width="10%">Selected</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {getAssetSummary.map((summary, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{summary.category}</TableCell>
                  <TableCell>{summary.name}</TableCell>
                  <TableCell align="center">{summary.requested}</TableCell>
                  <TableCell align="center">{summary.issued}</TableCell>
                  <TableCell align="center" style={{ color: summary.remaining > 0 ? '#2e7d32' : '#757575', fontWeight: 'bold' }}>
                    {summary.remaining}
                  </TableCell>
                  {assetsConfirmed && (
                    <TableCell align="center" style={{ 
                      color: summary.selected <= summary.remaining ? '#2e7d32' : '#d32f2f',
                      fontWeight: 'bold'
                    }}>
                      {summary.selected}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </SelectedAssetsContainer>

      {/* Display confirmed selected assets if any */}
      {assetsConfirmed && selectedAssets.length > 0 && (
        <SelectedAssetsContainer>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', width: '100%' }}>
            <SelectedAssetsTitle>Selected Assets for Issue ({selectedAssets.length})</SelectedAssetsTitle>
            <Tooltip title="Edit Selected Assets">
              <IconButton onClick={handleEditSelectedAssets} color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>
          </div>
          <TableContainer component={Paper} style={{ width: '100%' }}>
            <Table size="small" style={{ width: '100%', tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell width="5%">Sl.No.</TableCell>
                  <TableCell width="20%">Category</TableCell>
                  <TableCell width="25%">Asset Name</TableCell>
                  <TableCell width="25%">OEM Tag</TableCell>
                  <TableCell width="25%">ORG Tag</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedAssets.map((asset, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{asset.category}</TableCell>
                    <TableCell>{asset.name}</TableCell>
                    <TableCell>{asset.oem_tag || '-'}</TableCell>
                    <TableCell>{asset.org_tag || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SelectedAssetsContainer>
      )}

      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {/* Show Create Issue button only when assets are not confirmed */}
        {!assetsConfirmed && (
          <CreateIssueButton 
            onClick={handleOpenCreateIssueModal} 
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Create Issue'}
          </CreateIssueButton>
        )}
        
        <ViewAssetsButton 
          onClick={handleOpenViewAssetsModal}
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : 'View User Assets'}
        </ViewAssetsButton>
        
        {/* Show Forward button only when assets are confirmed */}
        {assetsConfirmed && selectedAssets.length > 0 && (
          <ForwardButton onClick={handleForwardWithSelectedAssets}>
            Forward to Level 2 Manager
          </ForwardButton>
        )}
        
        <RejectButton onClick={handleRejectRequest}>
          Reject Request
        </RejectButton>
      </div>

      {/* Create Issue Modal */}
      <Modal
        open={openCreateIssueModal}
        onClose={handleCloseCreateIssueModal}
        aria-labelledby="create-issue-modal"
      >
        <Box sx={ModalStyle}>
          <ModalTitle>Create Issue</ModalTitle>
          
          {/* Requested vs. Issued Assets Summary - Show in the modal too */}
          <div style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 10px 0' }}>Asset Request Summary</h4>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Category</TableCell>
                    <TableCell>Asset Name</TableCell>
                    <TableCell align="center">Requested</TableCell>
                    <TableCell align="center">Already Issued</TableCell>
                    <TableCell align="center">Remaining</TableCell>
                    <TableCell align="center">Selected</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getAssetSummary.map((summary, index) => (
                    <TableRow key={index}>
                      <TableCell>{summary.category}</TableCell>
                      <TableCell>{summary.name}</TableCell>
                      <TableCell align="center">{summary.requested}</TableCell>
                      <TableCell align="center">{summary.issued}</TableCell>
                      <TableCell align="center" style={{ color: summary.remaining > 0 ? '#2e7d32' : '#757575', fontWeight: 'bold' }}>
                        {summary.remaining}
                      </TableCell>
                      <TableCell align="center" style={{ 
                        color: summary.selected <= summary.remaining ? '#2e7d32' : '#d32f2f',
                        fontWeight: 'bold'
                      }}>
                        {summary.selected}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Available Assets Section with UnivMyAssetComp */}
          <div style={{ marginTop: '20px' }}>
            <InfoMessage>
              Select assets to issue from the available store inventory. You can use the filters to find specific assets.
            </InfoMessage>
            
            {openCreateIssueModal && (
              <UnivMyAssetComp
                key={`store-assets-selector-${modalInstanceId}`}
                columnFilter={["OEM/ORG Tag", "OEM/Brand", "Model", "Specification", "Detail"]}
                checkbox={true}
                selectedOptions={selectedAssets}
                setSelectedOptions={handleSelectedAssetsChange}
                selectAll={false}
                filterProps={storeFilterProps}
              />
            )}
          </div>

          <ModalFooter>
            <Button onClick={handleCancelSelection}>Cancel</Button>
            <ActionButton 
              onClick={handleConfirmSelectedAssets}
              disabled={selectedAssets.length === 0}
            >
              Confirm Selected Assets
            </ActionButton>
          </ModalFooter>
        </Box>
      </Modal>

      {/* View User Assets Modal */}
      <Modal
        open={openViewAssetsModal}
        onClose={handleCloseViewAssetsModal}
        aria-labelledby="view-assets-modal"
      >
        <Box sx={ModalStyle}>
          <ModalTitle>
            User Office Assets {row?.req_data?.[0]?.sent_by?.office && `(${row?.req_data?.[0]?.sent_by?.office})`}
          </ModalTitle>
          
          {openViewAssetsModal && (
            <UnivMyAssetComp
              key="user-assets-viewer"
              columnFilter={["OEM/ORG Tag", "OEM/Brand", "Model", "User", "Store", "Detail"]}
              checkbox={false}
              filterProps={userOfficeFilterProps}
            />
          )}

          <ModalFooter>
            <Button onClick={handleCloseViewAssetsModal}>Close</Button>
          </ModalFooter>
        </Box>
      </Modal>
    </>
  );
};

export default StoreL1InboxForm; 